import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Deviation, LatestAllocation, AllocationResult } from '../../types';
import { apiService } from '../../services/api';

interface ScenariosState {
  deviations: Deviation[];
  selectedId: string | null;
  history: AllocationResult[];
  latestAllocation: LatestAllocation | null;
  analyzedIds: string[];
  loading: boolean;
  error: string | null;
  historyLoading: boolean;

  // kept for backward compatibility if needed, but we rely on history now
  latestAllocationLoading: boolean;
  latestAllocationError: string | null;
}

const initialState: ScenariosState = {
  deviations: [],
  selectedId: null,
  history: [],
  latestAllocation: null,
  analyzedIds: [],
  loading: false,
  error: null,
  historyLoading: false,
  latestAllocationLoading: false,
  latestAllocationError: null,
};


export const fetchScenarios = createAsyncThunk(
  'scenarios/fetchScenarios',
  async (_, { rejectWithValue }) => {
    try {
      const scenarios = await apiService.fetchScenarios();
      return scenarios.map((s: any) => buildDeviationFromScenario(s));
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch scenarios');
    }
  }
);


export const fetchAllocationHistory = createAsyncThunk(
  'scenarios/fetchAllocationHistory',
  async (scenarioId: string | undefined, { rejectWithValue }) => {
    try {
      if (!scenarioId) return [];
      // Fetch all history for this scenario
      const history = await apiService.fetchAllocations(undefined, scenarioId);
      return history;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch allocation history');
    }
  }
);

// Fetch the global latest approved allocation (for the dashboard dropdown)
export const fetchLatestAllocation = createAsyncThunk(
  'scenarios/fetchLatestAllocation',
  async (scenarioId: string | undefined, { rejectWithValue }) => {
    try {
      // Preferred: latest approved allocation
      const approved = await apiService.fetchAllocations('approved', scenarioId);
      if (approved.length > 0) return approved[0];

      // Fallback: latest run regardless of approval status
      const anyStatus = await apiService.fetchAllocations(undefined, scenarioId);
      return anyStatus.length > 0 ? anyStatus[0] : null;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch latest allocation');
    }
  }
);

function buildDeviationFromScenario(scenario: any): Deviation {
  const batches = scenario.batches || [];
  const scenarioType = scenario.scenario_type || 'single';

  const firstBatch = batches[0] || scenario.deviation_batch || {};

  // Collect unique vehicle labels from all batches (modular approach)
  const allLabels: string[] = batches.length > 0
    ? Array.from(new Set(batches.map((b: any) => buildVehicleLabel(b.vehicle_spec))))
    : [buildVehicleLabel(firstBatch.vehicle_spec)];
  const vehicleLabel = allLabels.filter(l => l !== 'Vehicle').join(', ') || 'Vehicle';

  let title = '';

  if (scenarioType === 'mixed') {
    const surplusBatches = batches.filter((b: any) => b.deviation_type === 'surplus');
    const shortageBatches = batches.filter((b: any) => b.deviation_type === 'shortage');
    const surplusQty = surplusBatches.reduce((sum: number, b: any) => sum + (b.quantity || 0), 0);
    const shortageSupply = shortageBatches.reduce((sum: number, b: any) => sum + (b.quantity || 0), 0);

    const totalShortageDemand = scenario.total_demand || 0;
    const shortageGap = totalShortageDemand > 0 ? totalShortageDemand - shortageSupply : shortageSupply;

    const batchIds = batches.map((b: any) => b.id).join(', ');
    title = `${batchIds} | ${vehicleLabel} - Mixed (+${surplusQty} / -${shortageGap})`;
  } else if (scenarioType === 'multi-batch' && batches.length > 1) {
    const totalUnits = batches.reduce((sum: number, b: any) => sum + (b.quantity || 0), 0);
    const batchIds = batches.map((b: any) => b.id).join(', ');
    const devType = firstBatch.deviation_type || 'surplus';
    title = devType === 'shortage'
      ? `${batchIds} | ${vehicleLabel} - Shortage (${totalUnits})`
      : `${batchIds} | ${vehicleLabel} - Surplus (+${totalUnits})`;
  } else if (scenarioType === 'reconciliation') {
    const units = firstBatch.quantity || scenario.total_units || 0;
    const cleanName = scenario.name?.replace(/\s*\(\d[\d,]*\s*units\)/i, '') || 'Reconciliation';
    title = `Full Program: ${cleanName} (${units.toLocaleString()} units)`;
  } else {
    const batch = firstBatch;
    const batchId = batch.id || scenario.id;
    const devType = batch.deviation_type || 'surplus';

    if (devType === 'shortage') {
      const totalDemand = scenario.total_demand || 0;
      const supply = batch.quantity || 0;
      const shortfall = totalDemand > 0 ? totalDemand - supply : supply;
      title = vehicleLabel !== 'Vehicle'
        ? `${batchId} | ${vehicleLabel} - Shortage (-${shortfall.toLocaleString()})`
        : `${batchId}: Shortage (-${shortfall.toLocaleString()})`;
    } else {
      const units = batch.quantity || scenario.total_units || 0;
      title = vehicleLabel !== 'Vehicle'
        ? `${batchId} | ${vehicleLabel} - Surplus (+${units.toLocaleString()})`
        : `${batchId}: Surplus (+${units.toLocaleString()})`;
    }
  }

  return {
    id: scenario.id,
    scenario_id: scenario.scenario_id,
    title,
    scenario_type: scenarioType,
    batches: batches.length > 0 ? batches : [firstBatch],
    total_demand: scenario.total_demand,
    created_at: scenario.stored_at || scenario.created_at,
  };
}

function buildVehicleLabel(spec: any): string {
  if (!spec) return 'Vehicle';
  const parts = [];
  if (spec.color) parts.push(spec.color);
  if (spec.series) parts.push(spec.series);
  if (spec.powertrain) parts.push(spec.powertrain);
  return parts.length > 0 ? parts.join(' ') : 'Vehicle';
}

const scenariosSlice = createSlice({
  name: 'scenarios',
  initialState,
  reducers: {
    setSelectedId(state, action: PayloadAction<string>) {
      state.selectedId = action.payload;
    },
    markAsAnalyzed(state, action: PayloadAction<string>) {

      if (!state.analyzedIds.includes(action.payload)) {
        state.analyzedIds.push(action.payload);
      }
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScenarios.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScenarios.fulfilled, (state, action) => {
        state.loading = false;
        state.deviations = action.payload;
        if (!state.selectedId && action.payload.length > 0) {
          state.selectedId = action.payload[0].id;
        }
      })
      .addCase(fetchScenarios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchLatestAllocation.pending, (state) => {
        state.latestAllocationLoading = true;
        state.latestAllocationError = null;
      })
      .addCase(fetchLatestAllocation.fulfilled, (state, action) => {
        state.latestAllocationLoading = false;
        state.latestAllocation = action.payload;
      })
      .addCase(fetchLatestAllocation.rejected, (state, action) => {
        state.latestAllocationLoading = false;
        state.latestAllocationError = action.payload as string;
      })
      .addCase(fetchAllocationHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchAllocationHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload;
        // Optimization: Pre-fill latestAllocation from history if available and approved
        const approved = action.payload.find((r: AllocationResult) => r.approval_status === 'approved');
        if (approved) {
          state.latestAllocation = approved as LatestAllocation;
        }
      })
      .addCase(fetchAllocationHistory.rejected, (state) => {
        state.historyLoading = false;
      });
  },
});

export const selectAnalyzedIds = (state: { scenarios: ScenariosState }) =>
  state.scenarios.analyzedIds;

export const { setSelectedId, markAsAnalyzed, clearError } = scenariosSlice.actions;
export default scenariosSlice.reducer;
